import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { mastersApi } from '../../services/expenseMastersApi';
import BillList from './BillList';

// Keep a stored (possibly inactive) reference selectable when editing a draft
const withCurrent = (options, value, label) => {
  if (!value || options.some((o) => o.value === value)) return options;
  return [...options, { value, label: `${label || 'Unavailable'} (inactive)` }];
};

// Combined option values for the single Vendor and Payment Type dropdowns
export const vendorValue = (line) => {
  if (line.vendorKind === 'SELF') return 'SELF';
  if (line.vendorKind === 'VENDOR' && line.vendorId) return `VENDOR:${line.vendorId}`;
  if (line.vendorKind === 'EMPLOYEE' && line.vendorUserId) return `EMPLOYEE:${line.vendorUserId}`;
  return '';
};

export const paymentValue = (line) => (
  line.paymentType === 'PAID_VIA' ? (line.paidViaId ? `PAID_VIA:${line.paidViaId}` : '') : line.paymentType || ''
);

export const parsePaymentValue = (value) => (
  value.startsWith('PAID_VIA:')
    ? { paymentType: 'PAID_VIA', paidViaId: value.slice('PAID_VIA:'.length) }
    : { paymentType: value, paidViaId: '' }
);

// erp_expense "Vendor": one dropdown with "<self> (Internal)" preselected, the vendor
// master, and other employees as "<name> (Internal)". Vendors are searched server-side.
const VendorPicker = ({ line, selfName, users, onChange, error, helperText, disabled, index }) => {
  const [input, setInput] = useState('');
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      setLoading(true);
      mastersApi.list('vendors', { search: input.trim(), limit: 50 })
        .then((res) => {
          if (!cancelled) setVendors((res.data || []).map((v) => ({ value: `VENDOR:${v._id}`, label: v.name })));
        })
        .catch(() => { if (!cancelled) setVendors([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [input]);

  const value = vendorValue(line);
  const options = useMemo(() => {
    const term = input.trim().toLowerCase();
    const self = { value: 'SELF', label: `${selfName} (Internal)` };
    const employees = users
      .filter((u) => !term || u.label.toLowerCase().includes(term))
      .map((u) => ({ value: `EMPLOYEE:${u.value}`, label: `${u.label} (Internal)` }));
    const list = [...(!term || self.label.toLowerCase().includes(term) ? [self] : []), ...vendors, ...employees];
    if (!value || list.some((o) => o.value === value)) return list;
    const stored = value === 'SELF' ? self : { value, label: line.vendorKind === 'EMPLOYEE' ? `${line.vendorName} (Internal)` : line.vendorName };
    return [...list, stored];
  }, [input, selfName, users, vendors, value, line.vendorKind, line.vendorName]);

  const current = value ? options.find((o) => o.value === value) || null : null;

  const select = (opt) => {
    if (!opt) return onChange({ vendorKind: '', vendorId: '', vendorUserId: '', vendorName: '' });
    const [kind, id] = opt.value.split(':');
    const name = opt.label.replace(/ \(Internal\)$/, '');
    if (kind === 'VENDOR') return onChange({ vendorKind: 'VENDOR', vendorId: id, vendorUserId: '', vendorName: name });
    if (kind === 'EMPLOYEE') return onChange({ vendorKind: 'EMPLOYEE', vendorId: '', vendorUserId: id, vendorName: name });
    return onChange({ vendorKind: 'SELF', vendorId: '', vendorUserId: '', vendorName: '' });
  };

  return (
    <Autocomplete
      size="small"
      options={options}
      value={current}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x}
      onInputChange={(e, v, reason) => { if (reason === 'input') setInput(v); else if (reason === 'reset' || reason === 'clear') setInput(''); }}
      onChange={(e, opt) => select(opt)}
      getOptionLabel={(o) => o.label || ''}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      noOptionsText="No vendors found"
      renderInput={(params) => (
        <TextField
          {...params}
          label="Vendor"
          required
          error={error}
          helperText={helperText}
          inputProps={{ ...params.inputProps, 'data-testid': `vendor-${index}` }}
        />
      )}
    />
  );
};

// One expense line of a draft voucher. Field order and labels follow the erp_expense
// "Edit Voucher" add-expense row.
//   line:     form state (see ExpenseVoucherForm fromServerLine)
//   errors:   server/client error map keyed "<field>" for this line
//   lookups:  { categories, operatingCountries, countries, currencies, paidVia, users, headsFor, selfName }
const ExpenseLineEditor = ({
  index,
  line,
  errors = {},
  lookups,
  paymentTypeOptions,
  allowPaidVia = true,
  onChange,
  onRemove,
  canRemove,
  voucherId,
  billRules,
  onUploadBills,
  onDeleteBill,
  disabled,
}) => {
  const heads = lookups.headsFor(line.categoryId);
  const headOptions = useMemo(
    () => withCurrent(heads.map((h) => ({ value: h._id, label: h.name })), line.headId, line.headName),
    [heads, line.headId, line.headName]
  );
  const err = (field) => errors[field];
  const vendorError = err('vendorId') || err('vendorUserId') || err('vendorKind') || err('vendor');
  const paymentError = err('paymentType') || err('paidViaId');

  const field = (name, label, extra = {}) => ({
    size: 'small',
    fullWidth: true,
    label,
    value: line[name] ?? '',
    error: Boolean(err(name)),
    helperText: err(name) || extra.helperText,
    disabled,
    onChange: (e) => onChange({ [name]: e.target.value }),
    ...extra,
  });

  // erp_expense: selecting an Employment Country selects that country's currency
  const changeEmploymentCountry = (countryId) => {
    const nextDefault = lookups.countries.find((c) => c._id === countryId)?.defaultCurrency;
    onChange(nextDefault ? { employmentCountryId: countryId, currency: nextDefault } : { employmentCountryId: countryId });
  };

  const expenseCountryOptions = withCurrent(
    lookups.countries.map((c) => ({ value: c._id, label: c.name })), line.expenseCountryId, line.expenseCountryName
  );

  // Payment types first, then the Paid Via instruments (erp_expense: one dropdown)
  const paymentOptions = [
    ...paymentTypeOptions(line.paymentType).filter((o) => o.value !== 'PAID_VIA'),
    ...withCurrent(
      (allowPaidVia ? lookups.paidVia : []).map((p) => ({ value: `PAID_VIA:${p.value}`, label: p.label })),
      line.paymentType === 'PAID_VIA' && line.paidViaId ? `PAID_VIA:${line.paidViaId}` : '',
      line.paidViaName
    ),
  ];
  const billOptional = line.paymentType === 'ADVANCED' || line.paymentType === 'NEW_ADVANCED';

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid={`expense-line-${index}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>Expense line {index + 1}</Typography>
        {canRemove && (
          <Tooltip title="Remove line">
            <span>
              <IconButton size="small" color="error" onClick={onRemove} disabled={disabled} aria-label={`Remove line ${index + 1}`}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={2}>
          <TextField {...field('expenseDate', 'Expense Date', { type: 'date', required: true, InputLabelProps: { shrink: true } })} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <VendorPicker
            line={line}
            index={index}
            selfName={lookups.selfName}
            users={lookups.users}
            disabled={disabled}
            error={Boolean(vendorError)}
            helperText={vendorError}
            onChange={onChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            {...field('categoryId', 'Expense Category', { select: true, required: true })}
            onChange={(e) => onChange({ categoryId: e.target.value, headId: '' })}
            SelectProps={{ inputProps: { 'data-testid': `category-${index}` } }}
          >
            {withCurrent(lookups.categories, line.categoryId, line.categoryName).map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            {...field('headId', 'Expense Head', {
              select: true,
              required: true,
              disabled: disabled || !line.categoryId,
              helperText: err('headId') || (!line.categoryId ? 'Select a category first' : (!headOptions.length ? 'No heads for this category' : undefined)),
            })}
            SelectProps={{ inputProps: { 'data-testid': `head-${index}` } }}
          >
            {headOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField {...field('amount', 'Expense Amount', { type: 'number', required: true, inputProps: { min: 1, step: 'any' } })} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            size="small"
            options={expenseCountryOptions}
            value={expenseCountryOptions.find((o) => o.value === line.expenseCountryId) || null}
            disabled={disabled}
            onChange={(e, opt) => onChange({ expenseCountryId: opt ? opt.value : '' })}
            getOptionLabel={(o) => o.label || ''}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            renderInput={(params) => (
              <TextField {...params} label="Expense Country" required error={Boolean(err('expenseCountryId'))} helperText={err('expenseCountryId')} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField {...field('employmentCountryId', 'Employment Country', { select: true, required: true })}
            onChange={(e) => changeEmploymentCountry(e.target.value)}
            SelectProps={{ inputProps: { 'data-testid': `employment-country-${index}` } }}>
            {withCurrent(lookups.operatingCountries, line.employmentCountryId, line.employmentCountryName).map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField {...field('currency', 'Currency', { select: true, required: true })}
            SelectProps={{ inputProps: { 'data-testid': `currency-${index}` } }}>
            {withCurrent(lookups.currencies, line.currency, line.currency).map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            {...field('paymentType', 'Payment Type', { select: true, required: true })}
            value={paymentValue(line)}
            error={Boolean(paymentError)}
            helperText={paymentError}
            onChange={(e) => onChange(parsePaymentValue(e.target.value))}
            SelectProps={{ inputProps: { 'data-testid': `payment-type-${index}` } }}
          >
            {paymentOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        </Grid>

        <Grid item xs={12} md={5}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
            Expense Bill{billOptional ? ' (optional for advances)' : ' (required to submit)'}
          </Typography>
          <BillList
            voucherId={voucherId}
            bills={line.bills}
            rules={billRules}
            onUpload={onUploadBills}
            onDelete={onDeleteBill}
            disabled={disabled}
            error={err('bills')}
          />
        </Grid>
        <Grid item xs={12} md={7}>
          <TextField {...field('description', 'Expense Description', { required: true, multiline: true, minRows: 3, inputProps: { maxLength: 2000 } })} />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ExpenseLineEditor;
